import { AUDIO_CONFIG } from "../config/audio.ts";
import type { SimulationEvent } from "../sim/events.ts";
import {
  ProceduralAudioBank,
  type ProceduralAmbience,
  type ProceduralVehicleAudio,
  type ProceduralVoice,
} from "./procedural.ts";
import { classifyAudioSurface } from "./surfaces.ts";
import type {
  AudioEnvironmentContext,
  AudioSystemStatus,
  AudioUpdateReport,
  BrowserAudioContextFactory,
  InteriorAcousticProfile,
  RiverIdentity,
  SuppressedAudioEvent,
  VehicleAudioContext,
  VehicleAudioLevels,
  VilniusAudioSystem,
} from "./types.ts";

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function defaultAudioContextFactory(): AudioContext | null {
  const browserGlobal = globalThis as typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };
  const Context = browserGlobal.AudioContext ??
    browserGlobal.webkitAudioContext;
  return Context === undefined ? null : new Context();
}

function validateEnvironment(
  environment: Readonly<AudioEnvironmentContext>,
): void {
  if (!Number.isSafeInteger(environment.tick) || environment.tick < 0) {
    throw new RangeError("Audio environment tick must be a non-negative integer");
  }
  if (!Number.isSafeInteger(environment.dayIndex)) {
    throw new RangeError("Audio environment dayIndex must be an integer");
  }
  if (
    !Number.isFinite(environment.timeOfDayHours) ||
    environment.timeOfDayHours < 0 ||
    environment.timeOfDayHours >= 24
  ) {
    throw new RangeError(
      "Audio environment timeOfDayHours must be within [0, 24)",
    );
  }
  if (!Number.isFinite(environment.elevationMetres)) {
    throw new RangeError("Audio environment elevationMetres must be finite");
  }
  for (const [label, value] of [
    ["exposure01", environment.exposure01],
    ["bellAudibility01", environment.bellAudibility01],
    ["riverProximity01", environment.riverProximity01],
    ["interiorMix01", environment.interiorMix01],
  ] as const) {
    if (!Number.isFinite(value) || value < 0 || value > 1) {
      throw new RangeError(`Audio environment ${label} must be within [0, 1]`);
    }
  }
}

function validateVehicle(vehicle: Readonly<VehicleAudioContext>): void {
  if (!Number.isSafeInteger(vehicle.tick) || vehicle.tick < 0) {
    throw new RangeError("Vehicle audio tick must be a non-negative integer");
  }
  for (const [label, value] of [
    ["speedMps", vehicle.speedMps],
    ["maximumForwardSpeedMps", vehicle.maximumForwardSpeedMps],
    ["longitudinalAccelerationMps2", vehicle.longitudinalAccelerationMps2],
  ] as const) {
    if (!Number.isFinite(value)) {
      throw new RangeError(`Vehicle audio ${label} must be finite`);
    }
  }
  if (vehicle.maximumForwardSpeedMps <= 0) {
    throw new RangeError("Vehicle audio maximumForwardSpeedMps must be positive");
  }
  for (const [label, value] of [
    ["wetness01", vehicle.wetness01],
    ["impactIntensity01", vehicle.impactIntensity01],
  ] as const) {
    if (!Number.isFinite(value) || value < 0 || value > 1) {
      throw new RangeError(`Vehicle audio ${label} must be within [0, 1]`);
    }
  }
}

function vehicleLevels(
  vehicle: Readonly<VehicleAudioContext> | null | undefined,
): VehicleAudioLevels {
  if (vehicle === null || vehicle === undefined) {
    return { engine: 0, tyres: 0, horn: 0, siren: 0, impactTriggered: false };
  }
  validateVehicle(vehicle);
  const speed = Math.abs(vehicle.speedMps);
  const speed01 = clamp01(speed / vehicle.maximumForwardSpeedMps);
  const load01 = clamp01(Math.abs(vehicle.longitudinalAccelerationMps2) / 6);
  const tyreSpeed01 = clamp01(
    (speed - AUDIO_CONFIG.vehicleTyreMinimumSpeedMps) /
      Math.max(1, vehicle.maximumForwardSpeedMps * 0.65),
  );
  const surfaceScale = vehicle.surfaceKind === "terrain"
    ? 1.15
    : vehicle.surfaceKind === "hardscape"
    ? 0.86
    : 1;
  return {
    engine: AUDIO_CONFIG.vehicleEngineGain * (
      AUDIO_CONFIG.vehicleEngineIdleLevel + speed01 * 0.6 + load01 * 0.24
    ),
    tyres: AUDIO_CONFIG.vehicleTyreGain * tyreSpeed01 * surfaceScale *
      (1 + vehicle.wetness01 * 0.32),
    horn: vehicle.hornActive ? AUDIO_CONFIG.vehicleHornGain : 0,
    siren: vehicle.sirenActive ? AUDIO_CONFIG.vehicleSirenGain : 0,
    impactTriggered: false,
  };
}

function ambienceLevels(
  environment: Readonly<AudioEnvironmentContext>,
): Readonly<{ wind: number; river: number; interior: number }> {
  const elevationInfluence = clamp01(
    (
      environment.elevationMetres -
      AUDIO_CONFIG.windElevationFloorMetres
    ) / AUDIO_CONFIG.windElevationRangeMetres,
  );
  const windSeasonScale =
    AUDIO_CONFIG.windSeasonGainScale[environment.season];
  const riverSeasonScale =
    AUDIO_CONFIG.riverSeasonGainScale[environment.season];
  const exteriorMix = 1 - environment.interiorMix01;
  return {
    wind:
      AUDIO_CONFIG.windGain *
      windSeasonScale *
      environment.exposure01 *
      (0.35 + elevationInfluence * 0.65) *
      (0.08 + exteriorMix * 0.92),
    river: environment.nearestRiver === null
      ? 0
      : AUDIO_CONFIG.riverGain *
        riverSeasonScale *
        environment.riverProximity01 *
        (0.03 + exteriorMix * 0.97),
    interior: environment.interiorProfile === null
      ? 0
      : AUDIO_CONFIG.interiorGain[environment.interiorProfile] *
        environment.interiorMix01,
  };
}

function bellSlot(
  environment: Readonly<AudioEnvironmentContext>,
): number {
  return (
    environment.dayIndex * 24 * AUDIO_CONFIG.bellSlotsPerHour +
    Math.floor(
      environment.timeOfDayHours * AUDIO_CONFIG.bellSlotsPerHour,
    )
  );
}

function compareEvents(
  left: Readonly<SimulationEvent>,
  right: Readonly<SimulationEvent>,
): number {
  return left.tick - right.tick || left.id.localeCompare(right.id);
}

export interface CreateBrowserAudioSystemOptions {
  contextFactory?: BrowserAudioContextFactory;
}

export function createBrowserAudioSystem(
  options: Readonly<CreateBrowserAudioSystemOptions> = {},
): VilniusAudioSystem {
  return new BrowserAudioSystem(
    options.contextFactory ?? defaultAudioContextFactory,
  );
}

class BrowserAudioSystem implements VilniusAudioSystem {
  readonly #contextFactory: BrowserAudioContextFactory;
  readonly #activeOneShots = new Set<ProceduralVoice>();
  readonly #rememberedEventIds = new Map<SimulationEvent["id"], true>();
  readonly #lastEventTickByCooldownKey = new Map<string, number>();
  #context: AudioContext | null = null;
  #master: GainNode | null = null;
  #bank: ProceduralAudioBank | null = null;
  #wind: ProceduralAmbience | null = null;
  #river: ProceduralAmbience | null = null;
  #riverIdentity: RiverIdentity | null = null;
  #interior: ProceduralAmbience | null = null;
  #vehicleAudio: ProceduralVehicleAudio | null = null;
  #interiorProfile: InteriorAcousticProfile | null = null;
  #lastBellSlot: number | null = null;
  #lastVehicleId: string | null = null;
  #lastVehicleImpact01 = 0;
  #resumed = false;
  #muted = false;
  #disposed = false;

  constructor(contextFactory: BrowserAudioContextFactory) {
    this.#contextFactory = contextFactory;
  }

  async resume(): Promise<boolean> {
    if (this.#disposed) {
      return false;
    }
    if (this.#context === null) {
      let context: AudioContext | null;
      try {
        context = this.#contextFactory();
      } catch {
        return false;
      }
      if (context === null) {
        return false;
      }
      const master = context.createGain();
      master.gain.value = this.#muted ? 0 : AUDIO_CONFIG.masterGain;
      master.connect(context.destination);
      this.#context = context;
      this.#master = master;
      this.#bank = new ProceduralAudioBank(context, master);
    }

    try {
      if (this.#context.state === "suspended") {
        await this.#context.resume();
      }
      this.#resumed = this.#context.state === "running";
    } catch {
      this.#resumed = false;
    }
    return this.#resumed;
  }

  update(
    events: readonly Readonly<SimulationEvent>[],
    environment: Readonly<AudioEnvironmentContext>,
    vehicle?: Readonly<VehicleAudioContext> | null,
  ): AudioUpdateReport {
    validateEnvironment(environment);
    const levels = ambienceLevels(environment);
    const startedEventIds: SimulationEvent["id"][] = [];
    const suppressedEvents: SuppressedAudioEvent[] = [];
    let bellTriggered = false;
    const requestedVehicleLevels = vehicleLevels(vehicle);
    let impactTriggered = false;

    if (!this.#disposed) {
      this.#updateAmbience(levels, environment);
      for (const event of [...events].sort(compareEvents)) {
        const suppression = this.#suppressionFor(event);
        if (suppression !== null) {
          suppressedEvents.push({ id: event.id, reason: suppression });
          continue;
        }
        if (
          this.#bank === null ||
          !this.#resumed ||
          this.#context?.state !== "running"
        ) {
          suppressedEvents.push({ id: event.id, reason: "unavailable" });
          continue;
        }
        if (this.#muted) {
          suppressedEvents.push({ id: event.id, reason: "muted" });
          continue;
        }
        if (
          this.#activeOneShots.size >=
          AUDIO_CONFIG.maximumConcurrentOneShots
        ) {
          suppressedEvents.push({ id: event.id, reason: "voice-limit" });
          continue;
        }
        this.#startEvent(event, environment);
        startedEventIds.push(event.id);
      }
      bellTriggered = this.#updateBell(environment);
      impactTriggered = this.#updateVehicleAudio(vehicle, requestedVehicleLevels);
    }

    return {
      tick: environment.tick,
      startedEventIds,
      suppressedEvents,
      bellTriggered,
      ambience: levels,
      vehicle: { ...requestedVehicleLevels, impactTriggered },
    };
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    if (this.#master !== null && this.#context !== null) {
      this.#master.gain.setTargetAtTime(
        muted ? 0 : AUDIO_CONFIG.masterGain,
        this.#context.currentTime,
        AUDIO_CONFIG.muteSmoothingSeconds,
      );
      if (muted) {
        this.#wind?.setLevel(0, this.#context.currentTime);
        this.#river?.setLevel(0, this.#context.currentTime);
        this.#interior?.setLevel(0, this.#context.currentTime);
        this.#vehicleAudio?.update(
          { engine: 0, tyres: 0, horn: 0, siren: 0 },
          1,
          this.#context.currentTime,
        );
      }
    }
  }

  status(): AudioSystemStatus {
    return {
      available: this.#context !== null,
      resumed: this.#resumed,
      muted: this.#muted,
      disposed: this.#disposed,
      activeOneShots: this.#activeOneShots.size,
      rememberedEventIds: this.#rememberedEventIds.size,
      rememberedCooldownKeys: this.#lastEventTickByCooldownKey.size,
    };
  }

  async dispose(): Promise<void> {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#resumed = false;
    for (const voice of [...this.#activeOneShots]) {
      voice.stop();
    }
    this.#activeOneShots.clear();
    this.#wind?.dispose();
    this.#river?.dispose();
    this.#interior?.dispose();
    this.#vehicleAudio?.dispose();
    this.#wind = null;
    this.#river = null;
    this.#riverIdentity = null;
    this.#interior = null;
    this.#interiorProfile = null;
    this.#vehicleAudio = null;
    this.#lastVehicleId = null;
    this.#lastVehicleImpact01 = 0;
    this.#master?.disconnect();
    this.#master = null;
    this.#bank = null;
    const context = this.#context;
    this.#context = null;
    if (context !== null && context.state !== "closed") {
      try {
        await context.close();
      } catch {
        // Disposal remains terminal even if the browser rejects `close()`.
      }
    }
  }

  #suppressionFor(
    event: Readonly<SimulationEvent>,
  ): SuppressedAudioEvent["reason"] | null {
    if (this.#rememberedEventIds.has(event.id)) {
      return "duplicate";
    }
    this.#rememberEventId(event.id);
    const cooldownKey = `${event.type}:${event.playerId}`;
    const cooldownTicks = event.type === "surface-step"
      ? AUDIO_CONFIG.footstepCooldownTicks
      : AUDIO_CONFIG.landingCooldownTicks;
    const previousTick = this.#lastEventTickByCooldownKey.get(cooldownKey);
    if (
      previousTick !== undefined &&
      event.tick - previousTick < cooldownTicks
    ) {
      return "cooldown";
    }
    this.#rememberCooldownTick(cooldownKey, event.tick);
    return null;
  }

  #rememberEventId(id: SimulationEvent["id"]): void {
    this.#rememberedEventIds.set(id, true);
    while (
      this.#rememberedEventIds.size >
      AUDIO_CONFIG.rememberedEventIdLimit
    ) {
      const oldest = this.#rememberedEventIds.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      this.#rememberedEventIds.delete(oldest);
    }
  }

  #rememberCooldownTick(key: string, tick: number): void {
    this.#lastEventTickByCooldownKey.delete(key);
    this.#lastEventTickByCooldownKey.set(key, tick);
    while (
      this.#lastEventTickByCooldownKey.size >
      AUDIO_CONFIG.rememberedCooldownKeyLimit
    ) {
      const oldest = this.#lastEventTickByCooldownKey.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      this.#lastEventTickByCooldownKey.delete(oldest);
    }
  }

  #startEvent(
    event: Readonly<SimulationEvent>,
    environment: Readonly<AudioEnvironmentContext>,
  ): void {
    const bank = this.#bank;
    if (bank === null) {
      return;
    }
    let voice: ProceduralVoice | null = null;
    const onEnded = (): void => {
      if (voice !== null) {
        this.#activeOneShots.delete(voice);
      }
    };
    voice = event.type === "surface-step"
      ? bank.startFootstep(
          event.id,
          classifyAudioSurface(event.surfaceId, environment),
          onEnded,
        )
      : bank.startLanding(event.id, onEnded);
    this.#activeOneShots.add(voice);
  }

  #updateAmbience(
    levels: Readonly<{ wind: number; river: number; interior: number }>,
    environment: Readonly<AudioEnvironmentContext>,
  ): void {
    if (
      this.#disposed ||
      this.#muted ||
      !this.#resumed ||
      this.#context?.state !== "running" ||
      this.#bank === null
    ) {
      return;
    }
    if (levels.wind > 0 && this.#wind === null) {
      this.#wind = this.#bank.startWind();
    }
    if (
      environment.nearestRiver !== null &&
      this.#riverIdentity !== null &&
      environment.nearestRiver !== this.#riverIdentity
    ) {
      this.#river?.dispose();
      this.#river = null;
      this.#riverIdentity = null;
    }
    if (levels.river > 0 && this.#river === null) {
      const river = environment.nearestRiver;
      if (river !== null) {
        this.#river = this.#bank.startRiver(river);
        this.#riverIdentity = river;
      }
    }
    if (
      environment.interiorProfile !== this.#interiorProfile &&
      this.#interior !== null
    ) {
      this.#interior.dispose();
      this.#interior = null;
      this.#interiorProfile = null;
    }
    if (
      levels.interior > 0 &&
      environment.interiorProfile !== null &&
      this.#interior === null
    ) {
      this.#interior = this.#bank.startInterior(
        environment.interiorProfile,
      );
      this.#interiorProfile = environment.interiorProfile;
    }
    this.#wind?.setLevel(levels.wind, this.#context.currentTime);
    this.#river?.setLevel(levels.river, this.#context.currentTime);
    this.#interior?.setLevel(levels.interior, this.#context.currentTime);
  }

  #updateBell(
    environment: Readonly<AudioEnvironmentContext>,
  ): boolean {
    const slot = bellSlot(environment);
    const previousSlot = this.#lastBellSlot;
    this.#lastBellSlot = slot;
    if (
      previousSlot === null ||
      slot <= previousSlot ||
      environment.timeOfDayHours < AUDIO_CONFIG.bellStartHour ||
      environment.timeOfDayHours >= AUDIO_CONFIG.bellEndHour ||
      environment.bellAudibility01 <= 0 ||
      this.#muted ||
      !this.#resumed ||
      this.#context?.state !== "running" ||
      this.#bank === null ||
      this.#activeOneShots.size >= AUDIO_CONFIG.maximumConcurrentOneShots
    ) {
      return false;
    }
    let voice: ProceduralVoice | null = null;
    const onEnded = (): void => {
      if (voice !== null) {
        this.#activeOneShots.delete(voice);
      }
    };
    voice = this.#bank.startBell(
      `bell-slot:${slot}`,
      environment.bellAudibility01 *
        (
          environment.interiorProfile === null
            ? 1
            : 1 +
              (
                AUDIO_CONFIG.interiorBellGainScale[
                  environment.interiorProfile
                ] - 1
              ) * environment.interiorMix01
        ),
      onEnded,
    );
    this.#activeOneShots.add(voice);
    return true;
  }

  #updateVehicleAudio(
    vehicle: Readonly<VehicleAudioContext> | null | undefined,
    levels: Readonly<VehicleAudioLevels>,
  ): boolean {
    if (
      this.#disposed ||
      !this.#resumed ||
      this.#context?.state !== "running" ||
      this.#bank === null
    ) {
      return false;
    }
    if ((vehicle === null || vehicle === undefined) && this.#vehicleAudio === null) {
      return false;
    }
    this.#vehicleAudio ??= this.#bank.startVehicle();
    const activeLevels = this.#muted
      ? { engine: 0, tyres: 0, horn: 0, siren: 0 }
      : levels;
    const speed01 = vehicle === null || vehicle === undefined
      ? 0
      : clamp01(Math.abs(vehicle.speedMps) / vehicle.maximumForwardSpeedMps);
    const classPitch = vehicle === null || vehicle === undefined
      ? 1
      : AUDIO_CONFIG.vehicleEngineClassPitchScale[vehicle.vehicleClass];
    this.#vehicleAudio.update(
      activeLevels,
      (
        AUDIO_CONFIG.vehicleEngineMinimumPlaybackRate +
        speed01 * AUDIO_CONFIG.vehicleEnginePlaybackRateRange
      ) * classPitch,
      this.#context.currentTime,
    );

    if (vehicle === null || vehicle === undefined) {
      this.#lastVehicleId = null;
      this.#lastVehicleImpact01 = 0;
      return false;
    }
    if (vehicle.vehicleId !== this.#lastVehicleId) {
      this.#lastVehicleId = vehicle.vehicleId;
      this.#lastVehicleImpact01 = 0;
    }
    const impactTriggered =
      !this.#muted &&
      vehicle.impactIntensity01 >= AUDIO_CONFIG.vehicleImpactTrigger01 &&
      vehicle.impactIntensity01 > this.#lastVehicleImpact01 + 0.04 &&
      this.#activeOneShots.size < AUDIO_CONFIG.maximumConcurrentOneShots;
    this.#lastVehicleImpact01 = vehicle.impactIntensity01;
    if (!impactTriggered) return false;
    let voice: ProceduralVoice | null = null;
    const onEnded = (): void => {
      if (voice !== null) this.#activeOneShots.delete(voice);
    };
    voice = this.#bank.startVehicleImpact(vehicle.impactIntensity01, onEnded);
    this.#activeOneShots.add(voice);
    return true;
  }
}
